import {DateTime} from 'luxon';
import * as React from 'react';

export type MigrationTimelineBucket = {
  start: string;
  fromCount: number;
  toCount: number;
};

type Props = {
  /** Bucket size: 'month' | 'quarter' | 'year' (server-chosen). */
  unit: string;
  buckets: MigrationTimelineBucket[];
  fromName: string;
  toName: string;
  overlapStartDate: string | null;
  overlapEndDate: string | null;
};

// Series colors: blue-500 (moving in) and teal-600 (already there), validated
// for CVD separation and contrast on both light and dark surfaces. Identity
// is also position-encoded (above vs. below the axis), so color is redundant.
// Exported so MigrationOverlapPreview tags rows with the same encoding.
export const MIGRATION_FROM_COLOR = 'var(--color-blue-500, #2b7fff)';
export const MIGRATION_TO_COLOR = 'var(--color-teal-600, #009689)';
const OVERLAP_COLOR = 'var(--color-amber-500, #fe9a00)';

const HALF_HEIGHT = 32;
const AXIS_GAP = 3; // bar-to-axis breathing room on each side
const CHART_HEIGHT = HALF_HEIGHT * 2 + AXIS_GAP * 2 + 1;
const AXIS_Y = HALF_HEIGHT + AXIS_GAP;

const formatBucketLabel = (unit: string, startISO: string): string => {
  const start = DateTime.fromISO(startISO, {zone: 'utc'});
  switch (unit) {
    case 'year':
      return start.toFormat('yyyy');
    case 'quarter':
      return `Q${start.quarter} ${start.toFormat('yyyy')}`;
    default:
      return start.toFormat('MMM yyyy');
  }
};

/**
 * Indices of buckets that get an axis label: the first bucket plus each
 * year boundary, thinned to at most 8 so labels never collide. Spans with
 * fewer than three year boundaries (e.g. months within one year) fall back
 * to evenly spaced ticks instead.
 */
const tickIndices = (buckets: MigrationTimelineBucket[]): number[] => {
  const yearStarts = buckets
    .map((bucket, index) => ({index, year: bucket.start.slice(0, 4)}))
    .filter((entry, i, all) => i === 0 || entry.year !== all[i - 1].year)
    .map((entry) => entry.index);
  if (yearStarts.length >= 3) {
    const step = Math.ceil(yearStarts.length / 8);
    return yearStarts.filter((_, i) => i % step === 0);
  }
  const step = Math.max(1, Math.ceil(buckets.length / 6));
  return buckets.map((_, index) => index).filter((index) => index % step === 0);
};

/**
 * Tick label: full form (with year) on the first tick and at year
 * boundaries, short form (month/quarter only) for in-year filler ticks.
 */
const tickLabel = (
  unit: string,
  buckets: MigrationTimelineBucket[],
  index: number,
): string => {
  const isYearBoundary =
    index === 0 ||
    buckets[index].start.slice(0, 4) !== buckets[index - 1].start.slice(0, 4);
  if (isYearBoundary || unit === 'year') {
    return formatBucketLabel(unit, buckets[index].start);
  }
  const start = DateTime.fromISO(buckets[index].start, {zone: 'utc'});
  return unit === 'quarter' ? `Q${start.quarter}` : start.toFormat('MMM');
};

/**
 * Mirrored histogram of transaction counts per period: the source account's
 * transactions (which would move) above a shared time axis, the destination's
 * existing transactions below it. Columns with bars on both sides are periods
 * where the histories would interleave; when the accounts' date ranges
 * overlap, that window is marked on the axis in the warning color.
 */
export const MigrationTimeline = ({
  unit,
  buckets,
  fromName,
  toName,
  overlapStartDate,
  overlapEndDate,
}: Props) => {
  const [hovered, setHovered] = React.useState<number | null>(null);

  if (buckets.length === 0) {
    return null;
  }

  const maxCount = Math.max(
    1,
    ...buckets.map((bucket) => Math.max(bucket.fromCount, bucket.toCount)),
  );
  const barHeight = (count: number): number =>
    count === 0 ? 0 : Math.max(3, Math.round((count / maxCount) * HALF_HEIGHT));

  const columnPct = 100 / buckets.length;
  const barPct = Math.min(columnPct * 0.75, 6);
  const barX = (index: number): string =>
    `${index * columnPct + (columnPct - barPct) / 2}%`;

  // A bucket is inside the overlap window when its period intersects it.
  const bucketInOverlap = (bucket: MigrationTimelineBucket): boolean => {
    if (!overlapStartDate || !overlapEndDate) {
      return false;
    }
    const bucketEnd = DateTime.fromISO(bucket.start, {zone: 'utc'})
      .plus({[`${unit}s`]: 1})
      .toISODate();
    return (
      bucket.start <= overlapEndDate &&
      bucketEnd !== null &&
      bucketEnd > overlapStartDate
    );
  };
  const overlapIndices = buckets
    .map((bucket, index) => (bucketInOverlap(bucket) ? index : -1))
    .filter((index) => index >= 0);

  const contestedCount = buckets.filter(
    (bucket) => bucket.fromCount > 0 && bucket.toCount > 0,
  ).length;

  const readout =
    hovered !== null
      ? `${formatBucketLabel(unit, buckets[hovered].start)} — ${fromName}: ${
          buckets[hovered].fromCount
        } · ${toName}: ${buckets[hovered].toCount}`
      : contestedCount > 0
        ? `${contestedCount} ${
            contestedCount === 1 ? 'period has' : 'periods have'
          } transactions in both accounts`
        : 'No period has transactions in both accounts';

  return (
    <div className="flex flex-col gap-1">
      <div className="text-muted-foreground flex items-center gap-4 text-xs">
        <span className="flex min-w-0 items-center gap-1.5">
          <span
            className="size-2.5 shrink-0 rounded-sm"
            style={{backgroundColor: MIGRATION_FROM_COLOR}}
          />
          <span className="truncate">{fromName} (moving in) ↑</span>
        </span>
        <span className="flex min-w-0 items-center gap-1.5">
          <span
            className="size-2.5 shrink-0 rounded-sm"
            style={{backgroundColor: MIGRATION_TO_COLOR}}
          />
          <span className="truncate">{toName} (already there) ↓</span>
        </span>
      </div>

      <svg
        width="100%"
        height={CHART_HEIGHT}
        role="img"
        aria-label={`Transaction counts per ${unit} for both accounts`}
        onMouseLeave={() => setHovered(null)}
      >
        {hovered !== null && (
          <rect
            x={`${hovered * columnPct}%`}
            y={0}
            width={`${columnPct}%`}
            height={CHART_HEIGHT}
            fill="var(--color-muted)"
          />
        )}

        {/* axis */}
        <rect
          x="0"
          y={AXIS_Y}
          width="100%"
          height={1}
          fill="var(--color-border)"
        />
        {/* overlap window marker on the axis */}
        {overlapIndices.length > 0 && (
          <rect
            x={`${overlapIndices[0] * columnPct}%`}
            y={AXIS_Y - 0.5}
            width={`${(overlapIndices[overlapIndices.length - 1] - overlapIndices[0] + 1) * columnPct}%`}
            height={2}
            fill={OVERLAP_COLOR}
          />
        )}

        {buckets.map((bucket, index) => {
          const upHeight = barHeight(bucket.fromCount);
          const downHeight = barHeight(bucket.toCount);
          return (
            <React.Fragment key={bucket.start}>
              {upHeight > 0 && (
                <rect
                  x={barX(index)}
                  y={AXIS_Y - AXIS_GAP - upHeight}
                  width={`${barPct}%`}
                  height={upHeight}
                  rx={1.5}
                  fill={MIGRATION_FROM_COLOR}
                />
              )}
              {downHeight > 0 && (
                <rect
                  x={barX(index)}
                  y={AXIS_Y + 1 + AXIS_GAP}
                  width={`${barPct}%`}
                  height={downHeight}
                  rx={1.5}
                  fill={MIGRATION_TO_COLOR}
                />
              )}
              {/* hover hit target spanning the full column */}
              <rect
                x={`${index * columnPct}%`}
                y={0}
                width={`${columnPct}%`}
                height={CHART_HEIGHT}
                fill="transparent"
                onMouseEnter={() => setHovered(index)}
              />
            </React.Fragment>
          );
        })}
      </svg>

      <div className="text-muted-foreground relative h-4 text-[10px]">
        {tickIndices(buckets).map((index) => (
          <span
            key={buckets[index].start}
            className="absolute top-0 whitespace-nowrap"
            style={{left: `${index * columnPct}%`}}
          >
            {tickLabel(unit, buckets, index)}
          </span>
        ))}
      </div>

      <p className="text-muted-foreground m-0 h-4 truncate text-xs">
        {readout}
      </p>
    </div>
  );
};
